import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CommitImportInput,
  CommitImportResult,
  ImportPreview,
  ImportSession,
  UploadImportInput,
} from '@accounting/shared';
import { api } from '../lib/api';

export const importsKey = (companyId: string | null) =>
  ['imports', companyId ?? 'none'] as const;

export function useImportSessions(companyId: string | null) {
  return useQuery({
    queryKey: importsKey(companyId),
    enabled: companyId !== null,
    queryFn: () =>
      api
        .get<{ imports: ImportSession[] }>(`/companies/${companyId}/imports`)
        .then((r) => r.imports),
  });
}

export function useUploadImport(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UploadImportInput) => {
      if (!companyId) throw new Error('No active company');
      return api.post<ImportPreview>(`/companies/${companyId}/imports`, input);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: importsKey(companyId) });
    },
  });
}

export function useCommitImport(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: CommitImportInput }) => {
      if (!companyId) throw new Error('No active company');
      return api.post<CommitImportResult>(
        `/companies/${companyId}/imports/${id}/commit`,
        input,
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: importsKey(companyId) });
      qc.invalidateQueries({ queryKey: ['accounts', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: ['accounts-balances', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: ['journal-entries', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: ['report'] });
      qc.invalidateQueries({ queryKey: ['command-center'] });
    },
  });
}

/** Re-fetch the preview for an existing session so the wizard can be re-opened. */
export function useResumeImport(companyId: string | null) {
  return useMutation({
    mutationFn: (id: string) => {
      if (!companyId) throw new Error('No active company');
      return api.get<ImportPreview>(`/companies/${companyId}/imports/${id}`);
    },
  });
}

/** Discard a non-committed session. */
export function useDeleteImport(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => {
      if (!companyId) throw new Error('No active company');
      return api.delete<void>(`/companies/${companyId}/imports/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: importsKey(companyId) });
    },
  });
}

export function useRollbackImport(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => {
      if (!companyId) throw new Error('No active company');
      return api.post<{ session: ImportSession }>(
        `/companies/${companyId}/imports/${id}/rollback`,
        {},
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: importsKey(companyId) });
      qc.invalidateQueries({ queryKey: ['accounts', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: ['journal-entries', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: ['report'] });
    },
  });
}
